import { envValue, type EnvRecord } from "./envPreflight";

/**
 * M1. Whether the memory save-gate writes `contact_memory` rows. The one
 * reader of ACKRA_ENABLE_MEMORY_WRITES, here so that the worker
 * (`apps/worker/src/memoryWritesPlan.ts`, which re-exports it and decides once
 * at boot) and the operator's backfill (`pnpm memory:backfill`, loop f item
 * MBF) read the flag with the same parser and refuse the same values.
 *
 * CLAUDE.md section 6: memory writes are an explicit, logged decision. The
 * gate (`packages/agents/src/memorySaveGateHandler.ts`) logs that decision for
 * every customer DM whatever this says. This decides only whether the rows it
 * decided on are written, and the default is no: SHADOW, the decision event
 * and nothing else.
 *
 *  - ACKRA_ENABLE_MEMORY_WRITES is on only when it is exactly "1". Unset,
 *    empty or "0" is shadow. Anything else ("true", "yes", " 1") throws, the
 *    way ACKRA_ENABLE_TRANSMIT does: an intent this code cannot read is never
 *    guessed on, because on is the guess that writes what a customer said
 *    into what every later reply reads (section 0).
 *
 * The worker's `envSpec.ts` refuses the same shapes in the preflight, first
 * and all at once. `planMemoryWrites` is the backstop that also returns the
 * value the caller uses.
 */

export const ENABLE_MEMORY_WRITES_ENV = "ACKRA_ENABLE_MEMORY_WRITES";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateEnableMemoryWrites(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (memory writes on) or "0" (shadow). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function memoryWritesRequested(env: EnvRecord): boolean {
  return envValue(env, ENABLE_MEMORY_WRITES_ENV) !== undefined && env[ENABLE_MEMORY_WRITES_ENV] === "1";
}

export function planMemoryWrites(env: EnvRecord): boolean {
  if (envValue(env, ENABLE_MEMORY_WRITES_ENV) === undefined) return false;
  const complaint = validateEnableMemoryWrites(env[ENABLE_MEMORY_WRITES_ENV] ?? "");
  if (complaint !== null) throw new Error(`${ENABLE_MEMORY_WRITES_ENV} ${complaint}.`);
  return env[ENABLE_MEMORY_WRITES_ENV] === "1";
}
